import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiClient } from '../lib/auth';
import { toggleInSet } from '../lib/collections';
import type { MediaItem } from './mediaSlice';

/**
 * collectionsSlice — the current internal user's named, private collections.
 *
 * Mirrors `mediaSlice` (createAsyncThunk + apiClient + selectors). Membership
 * add/remove is applied optimistically via the pure `toggleInSet` and rolled
 * back when the thunk rejects, so the checkbox state in the modal stays
 * consistent with the stored state (R12.2). Validation errors from
 * create/rename surface in `error` for inline display.
 *
 * Requirements: 4.1, 5.1, 6.1, 7.1, 7.2, 8.1, 8.4, 9.1, 9.6, 12.1, 12.2, 12.3
 */

export interface Collection {
  collectionId: string;
  name: string;
  itemCount: number;
  createdAt?: string;
}

export interface CollectionsState {
  collections: Collection[];
  loading: boolean;
  error: string | null;
  detail: {
    collectionId: string | null;
    name: string;
    items: MediaItem[];
    loading: boolean;
    error: string | null;
  };
  /** mediaId -> collectionIds the media is a member of. */
  membership: Record<string, string[]>;
}

const initialState: CollectionsState = {
  collections: [],
  loading: false,
  error: null,
  detail: {
    collectionId: null,
    name: '',
    items: [],
    loading: false,
    error: null,
  },
  membership: {},
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function errMessage(err: unknown, fallback: string): string {
  // apiClient (axios) surfaces the API's error body under response.data.message.
  const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
  return anyErr?.response?.data?.message ?? anyErr?.message ?? fallback;
}

// ---------------------------------------------------------------------------
// Response shapes
// ---------------------------------------------------------------------------

interface CollectionListItem {
  collectionId: string;
  name: string;
  itemCount: number;
  createdAt?: string;
  /** present when GET /collections?mediaId= is used. */
  memberOfMediaId?: boolean;
}

interface CollectionListResponse {
  collections: CollectionListItem[];
}

interface CollectionDetailResponse {
  collection: { collectionId: string; name: string };
  items: MediaItem[];
}

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

/** GET /collections → list the user's collections with item counts. */
export const fetchCollections = createAsyncThunk(
  'collections/fetch',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<CollectionListResponse>('/collections');
      return response.data.collections ?? [];
    } catch (err: unknown) {
      return rejectWithValue(errMessage(err, 'Failed to load collections'));
    }
  },
);

/** POST /collections { name } → create a collection. */
export const createCollection = createAsyncThunk(
  'collections/create',
  async (name: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.post<Collection>('/collections', { name });
      return response.data;
    } catch (err: unknown) {
      return rejectWithValue(errMessage(err, 'Failed to create collection'));
    }
  },
);

/** PATCH /collections/{collectionId} { name } → rename a collection. */
export const renameCollection = createAsyncThunk(
  'collections/rename',
  async ({ collectionId, name }: { collectionId: string; name: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch<{ collectionId: string; name: string }>(
        `/collections/${collectionId}`,
        { name },
      );
      return response.data;
    } catch (err: unknown) {
      return rejectWithValue(errMessage(err, 'Failed to rename collection'));
    }
  },
);

/** DELETE /collections/{collectionId} → delete a collection. */
export const deleteCollection = createAsyncThunk(
  'collections/delete',
  async (collectionId: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/collections/${collectionId}`);
      return collectionId;
    } catch (err: unknown) {
      return rejectWithValue(errMessage(err, 'Failed to delete collection'));
    }
  },
);

/** GET /collections/{collectionId} → collection detail with its media. */
export const fetchCollectionDetail = createAsyncThunk(
  'collections/fetchDetail',
  async (collectionId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<CollectionDetailResponse>(
        `/collections/${collectionId}`,
      );
      return response.data;
    } catch (err: unknown) {
      return rejectWithValue(errMessage(err, 'Failed to load collection'));
    }
  },
);

/** POST /collections/{collectionId}/items { mediaId } → add media to collection. */
export const addToCollection = createAsyncThunk(
  'collections/addItem',
  async ({ collectionId, mediaId }: { collectionId: string; mediaId: string }, { rejectWithValue }) => {
    try {
      await apiClient.post(`/collections/${collectionId}/items`, { mediaId });
      return { collectionId, mediaId };
    } catch (err: unknown) {
      return rejectWithValue({
        collectionId,
        mediaId,
        message: errMessage(err, 'Failed to add to collection'),
      });
    }
  },
);

/** DELETE /collections/{collectionId}/items/{mediaId} → remove media from collection. */
export const removeFromCollection = createAsyncThunk(
  'collections/removeItem',
  async ({ collectionId, mediaId }: { collectionId: string; mediaId: string }, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/collections/${collectionId}/items/${mediaId}`);
      return { collectionId, mediaId };
    } catch (err: unknown) {
      return rejectWithValue({
        collectionId,
        mediaId,
        message: errMessage(err, 'Failed to remove from collection'),
      });
    }
  },
);

/** GET /collections?mediaId= → per-collection membership flags for a media item. */
export const fetchMembership = createAsyncThunk(
  'collections/fetchMembership',
  async (mediaId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<CollectionListResponse>('/collections', {
        params: { mediaId },
      });
      const collectionIds = (response.data.collections ?? [])
        .filter((c) => c.memberOfMediaId === true)
        .map((c) => c.collectionId);
      return { mediaId, collectionIds, collections: response.data.collections ?? [] };
    } catch (err: unknown) {
      return rejectWithValue(errMessage(err, 'Failed to load membership'));
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const collectionsSlice = createSlice({
  name: 'collections',
  initialState,
  reducers: {
    /** Clear any surfaced error (e.g. after showing a validation message). */
    clearCollectionsError(state) {
      state.error = null;
    },
    /**
     * Optimistically toggle a media item's membership in a collection. Updates
     * `membership[mediaId]` immediately via the pure `toggleInSet`. Rolled back
     * by the matching thunk's rejected case (R12.2).
     */
    optimisticSetMembership(
      state,
      action: PayloadAction<{ mediaId: string; collectionId: string; next: boolean }>,
    ) {
      const { mediaId, collectionId, next } = action.payload;
      const current = state.membership[mediaId] ?? [];
      state.membership[mediaId] = Array.from(toggleInSet(current, collectionId, next));
    },
  },
  extraReducers: (builder) => {
    // fetchCollections
    builder
      .addCase(fetchCollections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCollections.fulfilled, (state, action) => {
        state.collections = action.payload.map((c) => ({
          collectionId: c.collectionId,
          name: c.name,
          itemCount: c.itemCount,
          createdAt: c.createdAt,
        }));
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchCollections.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to load collections';
      });

    // createCollection — prepend on success; surface validation error on reject.
    builder
      .addCase(createCollection.fulfilled, (state, action) => {
        const c = action.payload;
        state.collections.unshift({
          collectionId: c.collectionId,
          name: c.name,
          itemCount: c.itemCount ?? 0,
          createdAt: c.createdAt,
        });
        state.error = null;
      })
      .addCase(createCollection.rejected, (state, action) => {
        state.error = (action.payload as string) ?? 'Failed to create collection';
      });

    // renameCollection
    builder
      .addCase(renameCollection.fulfilled, (state, action) => {
        const target = state.collections.find(
          (c) => c.collectionId === action.payload.collectionId,
        );
        if (target) {
          target.name = action.payload.name;
        }
        if (state.detail.collectionId === action.payload.collectionId) {
          state.detail.name = action.payload.name;
        }
        state.error = null;
      })
      .addCase(renameCollection.rejected, (state, action) => {
        state.error = (action.payload as string) ?? 'Failed to rename collection';
      });

    // deleteCollection
    builder
      .addCase(deleteCollection.fulfilled, (state, action) => {
        state.collections = state.collections.filter(
          (c) => c.collectionId !== action.payload,
        );
        state.error = null;
      })
      .addCase(deleteCollection.rejected, (state, action) => {
        state.error = (action.payload as string) ?? 'Failed to delete collection';
      });

    // fetchCollectionDetail
    builder
      .addCase(fetchCollectionDetail.pending, (state, action) => {
        state.detail.loading = true;
        state.detail.error = null;
        state.detail.collectionId = action.meta.arg;
      })
      .addCase(fetchCollectionDetail.fulfilled, (state, action) => {
        state.detail.loading = false;
        state.detail.error = null;
        state.detail.collectionId = action.payload.collection.collectionId;
        state.detail.name = action.payload.collection.name;
        state.detail.items = action.payload.items;
      })
      .addCase(fetchCollectionDetail.rejected, (state, action) => {
        state.detail.loading = false;
        state.detail.error = (action.payload as string) ?? 'Failed to load collection';
      });

    // addToCollection — optimistic apply already happened; roll back on reject.
    builder.addCase(addToCollection.rejected, (state, action) => {
      const payload = action.payload as
        | { collectionId: string; mediaId: string; message: string }
        | undefined;
      if (payload) {
        const current = state.membership[payload.mediaId] ?? [];
        state.membership[payload.mediaId] = Array.from(
          toggleInSet(current, payload.collectionId, false),
        );
        state.error = payload.message ?? 'Failed to add to collection';
      } else {
        state.error = 'Failed to add to collection';
      }
    });

    // removeFromCollection — optimistic apply already happened; roll back on reject.
    builder
      .addCase(removeFromCollection.fulfilled, (state, action) => {
        // When viewing the collection this item was removed from, drop it from
        // the detail list (R9.6).
        if (state.detail.collectionId === action.payload.collectionId) {
          state.detail.items = state.detail.items.filter(
            (i) => i.mediaId !== action.payload.mediaId,
          );
        }
      })
      .addCase(removeFromCollection.rejected, (state, action) => {
        const payload = action.payload as
          | { collectionId: string; mediaId: string; message: string }
          | undefined;
        if (payload) {
          const current = state.membership[payload.mediaId] ?? [];
          state.membership[payload.mediaId] = Array.from(
            toggleInSet(current, payload.collectionId, true),
          );
          state.error = payload.message ?? 'Failed to remove from collection';
        } else {
          state.error = 'Failed to remove from collection';
        }
      });

    // fetchMembership — record which collections the media belongs to, and keep
    // the collections list fresh if it came back with the response.
    builder.addCase(fetchMembership.fulfilled, (state, action) => {
      state.membership[action.payload.mediaId] = action.payload.collectionIds;
      if (action.payload.collections.length > 0 && state.collections.length === 0) {
        state.collections = action.payload.collections.map((c) => ({
          collectionId: c.collectionId,
          name: c.name,
          itemCount: c.itemCount,
          createdAt: c.createdAt,
        }));
      }
    });
  },
});

export const { clearCollectionsError, optimisticSetMembership } = collectionsSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectCollections = (state: { collections: CollectionsState }) =>
  state.collections.collections;
export const selectCollectionsLoading = (state: { collections: CollectionsState }) =>
  state.collections.loading;
export const selectCollectionsError = (state: { collections: CollectionsState }) =>
  state.collections.error;
export const selectCollectionDetail = (state: { collections: CollectionsState }) =>
  state.collections.detail;

/** Factory selector: the collectionIds the given media is a member of. */
export const selectMembership =
  (mediaId: string) =>
  (state: { collections: CollectionsState }): string[] =>
    state.collections.membership[mediaId] ?? [];

export default collectionsSlice.reducer;
